import {
  LISTING_BUTTON_KINDS,
  type ListingButtonAvailability,
  type ListingButtonKind,
  MAX_LISTING_BUTTONS,
} from './dto/listing-buttons'

export interface ListingButtonFacts {
  phone: string | null
  website: string | null
  instagram: string | null
  hasCoordinates: boolean
  /** The Mittagsmenü shows today (see `lunchMenuView`). */
  lunchMenu: boolean
  /** A Speisekarte is published. */
  fullMenu: boolean
}

const present = (value: string | null) => value !== null && value.trim().length > 0

export function listingButtonAvailability(facts: ListingButtonFacts): ListingButtonAvailability {
  return {
    lunch_menu: facts.lunchMenu,
    menu: facts.fullMenu,
    phone: present(facts.phone),
    directions: facts.hasCoordinates,
    website: present(facts.website),
    instagram: instagramUrl(facts.instagram) !== null,
    share: true,
  }
}

const isKind = (value: string): value is ListingButtonKind =>
  (LISTING_BUTTON_KINDS as readonly string[]).includes(value)

/**
 * The buttons a place page shows: the owner's choice in their order, or with no choice the first
 * four available kinds. Kinds that are not available are skipped and nothing is filled in.
 */
export function resolveListingButtons(
  chosen: readonly string[] | null,
  available: ListingButtonAvailability,
): ListingButtonKind[] {
  const candidates = chosen ?? LISTING_BUTTON_KINDS
  const resolved: ListingButtonKind[] = []
  for (const kind of candidates) {
    if (!isKind(kind) || !available[kind] || resolved.includes(kind)) continue
    resolved.push(kind)
    if (resolved.length === MAX_LISTING_BUTTONS) break
  }
  return resolved
}

const HANDLE = /^[A-Za-z0-9._]{1,30}$/
const INSTAGRAM_HOSTS = new Set([
  'instagram.com',
  'www.instagram.com',
  'm.instagram.com',
  'instagr.am',
])

function instagramLink(raw: string): URL | null {
  const absolute = /^https?:\/\//i.test(raw)
    ? raw
    : /^((www|m)\.)?(instagram\.com|instagr\.am)\//i.test(raw)
      ? `https://${raw}`
      : null
  if (!absolute) return null
  try {
    return new URL(absolute)
  } catch {
    return null
  }
}

/**
 * A link to the place's Instagram from a stored handle ("@cafe", "cafe") or an Instagram link,
 * without the tracking query the Instagram app adds when sharing; null for links elsewhere.
 */
export function instagramUrl(value: string | null | undefined): string | null {
  const raw = value?.trim()
  if (!raw) return null
  const link = instagramLink(raw)
  if (link) {
    const path = link.pathname.replace(/\/+$/, '')
    return INSTAGRAM_HOSTS.has(link.hostname.toLowerCase()) && path.length > 1
      ? `https://www.instagram.com${path}`
      : null
  }
  const handle = raw.replace(/^@/, '')
  return HANDLE.test(handle) ? `https://www.instagram.com/${handle}` : null
}
